import { FiDroplet, FiTablet, FiUser, FiWatch } from "react-icons/fi";

import { cx } from "@/lib/tailwind-utils";
import { formatWearableProvider } from "@/lib/wearable-connect";

interface MeasurementSourceBadgeProps {
  isUserReported?: boolean | null;
  isLabSourced?: boolean | null;
  isWearableSourced?: boolean | null;
  /**
   * The brand that delivered this value, as the aggregator names it ("OURA").
   * Names the device in the tooltip; absent on values recorded before values
   * carried a provider, which fall back to the unnamed wording.
   */
  wearableProvider?: string | null;
  /**
   * Set when more than one brand reported this field in this bucket. The number
   * shown is then the most recent single reading rather than a figure combined
   * across brands, so the tooltip names the situation instead of one device.
   */
  fromMultipleDevices?: boolean | null;
  isInbodySourced?: boolean | null;
  compact?: boolean;
  className?: string;
}

export function MeasurementSourceBadge({
  isUserReported,
  isLabSourced,
  isWearableSourced,
  wearableProvider,
  fromMultipleDevices,
  isInbodySourced,
  compact = false,
  className,
}: MeasurementSourceBadgeProps) {
  // Vendor-owned sources take precedence (a value has one origin, never two) and signal that
  // the value is read-only — the row hides its Edit affordance to match.
  //
  // Named, labelled and coloured for the machine rather than the category. "Device" is already
  // the POS card reader everywhere else in the product, and the wearables integration owns the
  // purple FiActivity badge for a reading off a patient's watch or smart scale — which is not
  // the same evidence as a scan taken on the clinic's own InBody and must not look like it.
  if (isInbodySourced) {
    return (
      <span
        data-testid="inbody-sourced-badge"
        title="Captured on an InBody device — read-only"
        className={cx(
          "inline-flex items-center gap-1 rounded-full border border-status-info-text/20 bg-status-info-bg px-1.5 py-0.5 text-tiny font-medium leading-tight text-status-info-text",
          className,
        )}
      >
        <FiTablet size={11} className="shrink-0" aria-hidden="true" />
        {compact ? <span>{"InBody"}</span> : <span>{"InBody scan"}</span>}
      </span>
    );
  }

  if (isLabSourced) {
    return (
      <span
        data-testid="lab-sourced-badge"
        title="Auto-tracked from a lab order — read-only"
        className={cx(
          "inline-flex items-center gap-1 rounded-full border border-status-success-text/20 bg-status-success-bg px-1.5 py-0.5 text-tiny font-medium leading-tight text-status-success-text",
          className,
        )}
      >
        <FiDroplet size={11} className="shrink-0" aria-hidden="true" />
        {compact ? <span>{"Lab"}</span> : <span>{"Lab result"}</span>}
      </span>
    );
  }

  // Device-sourced is the same kind of value as lab-sourced — vendor-owned, rewritten by the
  // next delivery covering that day — so it reads as read-only and the row hides Edit to match.
  if (isWearableSourced) {
    return (
      <span
        data-testid="device-sourced-badge"
        // The label stays the generic "Device" — this sits on every value row of a group
        // card, where a brand name is several times the width — so the device is named
        // only on hover. Dropping read-only from the named wording costs nothing: the row
        // hides Edit, and the card's edit affordance says so already.
        // Two brands reporting the same metric are never combined into one figure,
        // so the tooltip has to say which reading this is — naming a single brand
        // here would claim the other one's data was folded in.
        title={
          fromMultipleDevices
            ? "More than one connected device — showing the most recent reading, read-only"
            : wearableProvider
              ? formatWearableProvider(wearableProvider)
              : "Auto-tracked from a connected device — read-only"
        }
        className={cx(
          "inline-flex items-center gap-1 rounded-full border border-status-indigo-text/20 bg-status-indigo-bg px-1.5 py-0.5 text-tiny font-medium leading-tight text-status-indigo-text",
          className,
        )}
      >
        <FiWatch size={11} className="shrink-0" aria-hidden="true" />
        <span>{"Device"}</span>
      </span>
    );
  }

  if (!isUserReported) return null;

  return (
    <span
      data-testid="patient-reported-badge"
      title="Submitted by the patient through a form"
      className={cx(
        "inline-flex items-center gap-1 rounded-full border border-status-info-text/20 bg-status-info-bg px-1.5 py-0.5 text-tiny font-medium leading-tight text-status-info-text",
        className,
      )}
    >
      <FiUser size={11} className="shrink-0" aria-hidden="true" />
      {compact ? <span>{"Patient"}</span> : <span>{"Patient reported"}</span>}
    </span>
  );
}
